import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import AddressLink from './AddressLink';
import { getPercentageColor } from '../utils/riskUtils';

interface TokenDetails {
  mint: string;
  name: string;
  symbol: string;
  uri: string;
  creator: string;
  creatorNetwork: string;
  createdAt: string;
  description: string;
  telegram: string;
  twitter: string;
  website: string;
  decimals?: number;
  networkPercentage: string;
  riskLevel: string;
  networkBundles: Array<{
    address: string;
    name: string;
    percentOwnership: number;
    riskLevel: string;
    accounts: Array<{
      walletAddress: string;
      tokenAmount: string;
      percentage: number;
      topFundingSource: string;
      tokenAccountAddress: string;
      oldestTx: string;
      riskLevel: string;
      label: string;
      tags: string[];
      figureKey?: string | null;
      figureName?: string | null;
      figureConfidence?: 'confirmed' | 'probable';
      streamflowLocked?: string;
      streamflowLockedIn48h?: string;
      tibaneStaked?: string;
    }>;
  }>;
  individualHolders: Array<{
    walletAddress: string;
    tokenAmount: string;
    percentage: number;
    topFundingSource: string;
    tokenAccountAddress: string;
    riskLevel: string;
    streamflowLocked?: string;
    streamflowLockedIn48h?: string;
  }>;
  uniqueWalletCount: number;
  totalAccounts: number;
  topNetworkOwnership: number;
}

interface BundleNetworksProps {
  tokenDetails: TokenDetails;
}

const BundleNetworks: React.FC<BundleNetworksProps> = ({ tokenDetails }) => {
  const [expandedNetworks, setExpandedNetworks] = useState<Set<string>>(new Set());

  const toggleNetworkExpansion = (source: string) => {
    const newExpanded = new Set(expandedNetworks);
    if (newExpanded.has(source)) {
      newExpanded.delete(source);
    } else {
      newExpanded.add(source);
    }
    setExpandedNetworks(newExpanded);
  };

  const copyToClipboard = (address: string) => {
    navigator.clipboard.writeText(address);
  };

  const formatWalletAge = (oldestTx: string) => {
    if (!oldestTx) return 'Unknown';

    const txDate = new Date(oldestTx);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - txDate.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 30) return `${diffDays}d`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo`;
    return `${Math.floor(diffDays / 365)}y`;
  };

  const getAdjustedTokenAmount = (amount: string) => {
    const num = parseFloat(amount);
    if (isNaN(num)) return num;

    // Use decimals if available, otherwise assume 1e6 (10^6)
    const decimals = tokenDetails.decimals ?? 6;
    return num / Math.pow(10, decimals);
  };

  const formatTokenAmount = (amount: string) => {
    const adjustedAmount = getAdjustedTokenAmount(amount);
    if (isNaN(adjustedAmount)) return amount;

    if (adjustedAmount >= 1e12) return (adjustedAmount / 1e12).toFixed(1) + 'T';
    if (adjustedAmount >= 1e9) return (adjustedAmount / 1e9).toFixed(1) + 'B';
    if (adjustedAmount >= 1e6) return (adjustedAmount / 1e6).toFixed(1) + 'M';
    if (adjustedAmount >= 1e3) return (adjustedAmount / 1e3).toFixed(1) + 'K';
    return adjustedAmount.toLocaleString();
  };

  // Show every network ≥ 1% of supply, but always at least 5 entries
  // (so a token with only one >= 1% network still shows 5 for context).
  const sorted = [...tokenDetails.networkBundles].sort((a, b) => b.percentOwnership - a.percentOwnership);
  const overOnePct = sorted.filter(n => n.percentOwnership >= 1).length;
  const networkEntries = sorted.slice(0, Math.max(5, overOnePct));

  const getRiskColor = (riskLevel: string) => {
    if (riskLevel === 'EXTREME') return 'crypto-red';
    if (riskLevel === 'HIGH') return 'crypto-red';
    if (riskLevel === 'MEDIUM') return 'crypto-yellow';
    return 'crypto-accent';
  };

  const getRiskLevelText = (riskLevel: string) => {
    if (riskLevel === 'EXTREME') return 'Extreme Risk';
    if (riskLevel === 'HIGH') return 'High Risk';
    if (riskLevel === 'MEDIUM') return 'Medium Risk';
    return 'Low Risk';
  };

  const getRiskBgColor = (riskLevel: string) => {
    if (riskLevel === 'EXTREME') return 'crypto-red/20';
    if (riskLevel === 'HIGH') return 'crypto-red/20';
    if (riskLevel === 'MEDIUM') return 'crypto-yellow/20';
    return 'crypto-accent/20';
  };

  return (
    <section className="mb-8">
      <h2 className="text-2xl font-bold mb-4">Funding Network Holdings</h2>
      <div className="space-y-2">
        {networkEntries.map((network, index) => {
          const riskColor = getRiskColor(network.riskLevel);
          const riskLevelText = getRiskLevelText(network.riskLevel);
          const bgColor = getRiskBgColor(network.riskLevel);
          const isExpanded = expandedNetworks.has(network.address);
          const displayedWallets = network.accounts.slice(0, 10);
          const hasLocked = network.accounts.some(a => a.streamflowLocked && a.streamflowLocked !== '0');
          const hasStaked = network.accounts.some(a => a.tibaneStaked && a.tibaneStaked !== '0');

          return (
            <div key={network.address} className="bg-crypto-gray border border-gray-700 rounded-lg overflow-hidden">
              <div
                className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-700/20 transition-colors"
                onClick={() => toggleNetworkExpansion(network.address)}
              >
                <div className={`w-6 h-6 bg-${bgColor} text-${riskColor} rounded flex-shrink-0 flex items-center justify-center text-xs font-bold`}>
                  {index + 1}
                </div>
                <AddressLink
                  address={network.address}
                  chain={(tokenDetails as any).chain}
                  kind="network"
                  onClick={e => e.stopPropagation()}
                  className="text-white hover:text-crypto-accent transition-colors font-semibold truncate"
                >
                  {network.name}
                </AddressLink>
                <span className="text-gray-600">·</span>
                <span className="text-sm text-gray-400 whitespace-nowrap">{network.accounts.length} wallets</span>
                {hasLocked && (
                  <i className="fas fa-lock text-blue-400 text-xs" title="Includes Streamflow locked tokens"></i>
                )}
                {hasStaked && (
                  <i className="fas fa-coins text-purple-400 text-xs" title="Includes staked tokens via Tibane Labs"></i>
                )}
                <div className="flex-1" />
                <span className={`text-${riskColor} font-bold whitespace-nowrap`}>
                  {network.percentOwnership.toFixed(1)}%
                </span>
                <span className={`bg-${bgColor} text-${riskColor} px-2 py-0.5 rounded-full text-xs whitespace-nowrap`}>
                  {riskLevelText}
                </span>
                <i className={`fas fa-chevron-${isExpanded ? 'up' : 'down'} text-gray-400 text-xs w-3 text-center`}></i>
              </div>

              {isExpanded && (
                <div className="border-t border-gray-700 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-gray-400">
                      {Math.min(10, network.accounts.length)} out of {network.accounts.length} network wallets shown
                    </span>
                  </div>
                  <div className="bg-crypto-gray border border-gray-700 rounded-xl overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-crypto-dark border-b border-gray-700">
                          <tr>
                            <th className="text-left p-4 text-sm font-medium text-gray-400">Wallet</th>
                            <th className="text-left p-4 text-sm font-medium text-gray-400">% Held</th>
                            <th className="text-left p-4 text-sm font-medium text-gray-400">Token Amount</th>
                            <th className="text-left p-4 text-sm font-medium text-gray-400">Wallet Age</th>
                            <th className="text-left p-4 text-sm font-medium text-gray-400">Deep Funding Source</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-700">
                          {displayedWallets.map((account) => (
                            <tr key={account.tokenAccountAddress} className="hover:bg-crypto-dark/50 transition-colors">
                              <td className="p-4">
                                <div className="flex items-center space-x-2">
                                  {account.figureName && account.figureKey ? (
                                    account.figureConfidence === 'probable' ? (
                                      <Link
                                        to={`/figures/${account.figureKey}`}
                                        className="text-sm font-semibold text-amber-400 hover:underline inline-flex items-center gap-1.5"
                                        title={`Probable identity (unconfirmed lead) — ${account.figureName}`}
                                      >
                                        <i className="fas fa-user-tag text-xs text-amber-400" />
                                        {account.figureName}
                                        <span className="text-amber-400/70 text-[10px] font-normal">?</span>
                                      </Link>
                                    ) : (
                                      <Link
                                        to={`/figures/${account.figureKey}`}
                                        className="text-sm font-semibold text-crypto-accent hover:underline inline-flex items-center gap-1.5"
                                        title={`Known figure: ${account.figureName}`}
                                      >
                                        <i className="fas fa-user-check text-xs text-crypto-green" />
                                        {account.figureName}
                                      </Link>
                                    )
                                  ) : (
                                    <AddressLink
                                      address={account.walletAddress}
                                      chain={(tokenDetails as any).chain}
                                      className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                                    >
                                      {account.label || (account.walletAddress ? `${account.walletAddress.slice(0, 6)}...${account.walletAddress.slice(-4)}` : 'Unknown')}
                                    </AddressLink>
                                  )}
                                  {account.streamflowLocked && account.streamflowLocked !== '0' && (
                                    <span className="text-blue-400" title="Streamflow locked tokens">
                                      <i className="fas fa-lock text-xs"></i>
                                    </span>
                                  )}
                                  <button
                                    onClick={() => copyToClipboard(account.walletAddress || '')}
                                    className="text-gray-400 hover:text-white transition-colors"
                                  >
                                    <i className="fas fa-copy text-xs"></i>
                                  </button>
                                </div>
                              </td>
                              <td className="p-4">
                                <span className={`font-bold ${getPercentageColor(account.percentage || 0)}`}>
                                  {(account.percentage || 0) < 0.1 ? (account.percentage || 0).toFixed(3) : (account.percentage || 0).toFixed(2)}%
                                </span>
                              </td>
                              <td className="p-4">
                                <span className="text-white font-medium">{formatTokenAmount(account.tokenAmount)}</span>
                                {account.tibaneStaked && account.tibaneStaked !== '0' && (
                                  <div className="text-xs text-purple-400 mt-0.5">
                                    {(() => {
                                      const staked = parseFloat(account.tibaneStaked);
                                      const total = parseFloat(account.tokenAmount);
                                      const pct = total > 0 ? (staked / total * 100) : 0;
                                      return `${pct.toFixed(0)}% staked`;
                                    })()}
                                  </div>
                                )}
                              </td>
                              <td className="p-4">
                                <span className="text-white font-medium">{formatWalletAge(account.oldestTx)}</span>
                              </td>
                              <td className="p-4">
                                <div className="flex items-center space-x-2">
                                  <AddressLink
                                    address={account.topFundingSource}
                                    chain={(tokenDetails as any).chain}
                                    className="font-mono text-sm text-crypto-accent hover:text-crypto-accent/80 transition-colors"
                                  >
                                    {account.topFundingSource?.slice(0, 6)}...{account.topFundingSource?.slice(-4)}
                                  </AddressLink>
                                  <button
                                    onClick={() => copyToClipboard(account.topFundingSource || '')}
                                    className="text-gray-400 hover:text-white transition-colors"
                                  >
                                    <i className="fas fa-copy text-xs"></i>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default BundleNetworks;
